/**
 * Tiny cookie helper used by the cross-domain auth bridge.
 * No third-party dependencies — works in any browser context.
 */

import { CY_SSO_BASE_URL } from "../../Services/constants";
import { getTokenObj } from "../../Services/tokenManager";
import { decrypt } from "./others";

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

/**
 * Tells cy-sso to invalidate the session tied to the current access token.
 * Every clearAuthCookies() call site is immediately followed by a navigation
 * (window.location.replace/href), which can abort a plain XHR/Axios request
 * mid-flight. `fetch` with keepalive:true is built for exactly this — the
 * browser guarantees the request is sent even though the page is unloading.
 * (navigator.sendBeacon can't be used instead since it doesn't support
 * custom headers, and we need to send the token via Authorization.)
 * Still fire-and-forget: cookies/localStorage get cleared client-side
 * regardless of whether the backend call succeeds.
 */
const notifyBackendLogout = () => {
  const accessToken = getTokenObj()?.ACCESS_TOKEN;
  if (!accessToken) return;
  fetch(`${CY_SSO_BASE_URL}api/logout`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      "auth-source": "cy-sso",
    },
  }).catch(() => {});
};

/**
 * Sets a cookie.
 *
 * @param {string} name
 * @param {string} value             Raw string (caller should JSON.stringify if needed)
 * @param {object} [opts]
 * @param {string} [opts.domain]     Cookie Domain attribute (e.g. ".codeyoung.com")
 * @param {string} [opts.path="/"]
 * @param {number} [opts.maxAge]     Seconds until expiry
 * @param {string} [opts.sameSite="Lax"]  "Lax" | "Strict" | "None"
 * @param {boolean} [opts.secure=true]   Set Secure flag (auto-disabled on http://localhost)
 */
export const setCookie = (name, value, opts = {}) => {
  const {
    domain,
    path = "/",
    maxAge,
    sameSite = "Lax",
    secure = true,
  } = opts;

  const isHttpLocalhost =
    window.location.protocol === "http:" &&
    window.location.hostname === "localhost";

  const parts = [`${name}=${encodeURIComponent(value)}`, `Path=${path}`];
  if (domain) parts.push(`Domain=${domain}`);
  if (typeof maxAge === "number") parts.push(`Max-Age=${maxAge}`);
  parts.push(`SameSite=${sameSite}`);
  if (secure && !isHttpLocalhost) parts.push("Secure");

  document.cookie = parts.join("; ");
};

/**
 * Reads a cookie by name. Returns the decoded value, or null.
 */
export const getCookie = (name) => {
  const target = `${name}=`;
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const c of cookies) {
    if (c.startsWith(target)) {
      try {
        return decodeURIComponent(c.slice(target.length));
      } catch {
        return null;
      }
    }
  }
  return null;
};

/**
 * Deletes a cookie. MUST be called with the same Domain + Path used to set it.
 */
export const deleteCookie = (name, opts = {}) => {
  const { domain, path = "/" } = opts;
  const parts = [`${name}=`, `Path=${path}`, "Max-Age=0"];
  if (domain) parts.push(`Domain=${domain}`);
  document.cookie = parts.join("; ");
};

/**
 * Returns the candidate cookie Domain attributes a cookie on `hostname` could
 * have been set with: every parent suffix of at least two labels, dot-prefixed.
 *
 *   "sandbox.dev.codeyoung.com" → [".dev.codeyoung.com", ".codeyoung.com"]
 *   "localhost"                 → []   (host-only cookies have no Domain)
 */
const candidateCookieDomains = (hostname) => {
  if (!hostname || hostname === "localhost") return [];
  const labels = hostname.split(".");
  const domains = [];
  // Build suffixes from most-specific to least, keeping >= 2 labels.
  for (let i = 0; i <= labels.length - 2; i++) {
    domains.push("." + labels.slice(i).join("."));
  }
  return domains;
};

/**
 * THE canonical shared cookie Domain — the single source of truth for every
 * cross-app cookie write (auth tokens, `cy_auth_sync`, `cy_logged_out`). Derived
 * purely from the current host (drop the leftmost label), so it is deterministic,
 * ENV-INDEPENDENT, env-ISOLATED, and resolves to the SAME value in both apps:
 *
 *   sandbox.codeyoung.com / studentportal.codeyoung.com         → .codeyoung.com
 *   sandbox.dev.codeyoung.com / studentportal.dev.codeyoung.com → .dev.codeyoung.com
 *   codeyoung.com                                               → .codeyoung.com
 *   localhost                                                   → undefined (host-only)
 *
 * Both apps write on this one domain, so there are never duplicate same-named
 * cookies on different domains (which order unpredictably in document.cookie and
 * let a stale value win), and a dev write never lands on prod's `.codeyoung.com`.
 */
export const getSharedCookieDomain = () => {
  const host = window.location.hostname;
  if (host === "localhost") return undefined;
  const labels = host.split(".");
  if (labels.length >= 3) return "." + labels.slice(1).join(".");
  if (labels.length === 2) return "." + host;
  return undefined;
};

/**
 * Deletes a cookie across every domain it might have been set with — host-only
 * plus each parent suffix of the current hostname. Use this whenever the exact
 * Domain a cookie was written with isn't known at the delete site: a cookie set
 * with `Domain=.codeyoung.com` can only be removed by re-specifying that same
 * Domain, so a plain host-only `deleteCookie(name)` silently no-ops on it.
 */
export const deleteCookieAllDomains = (name) => {
  const domains = [undefined, ...candidateCookieDomains(window.location.hostname)];
  domains.forEach((domain) => deleteCookie(name, { domain }));
};

/**
 * Deletes the given auth cookies across every domain they might have been set
 * with — host-only plus each parent suffix of the current hostname. This is
 * needed because the same cookie may have been written host-only (localhost),
 * on the shared cross-app domain (`.dev.codeyoung.com`), and/or on
 * `.codeyoung.com` (via setAuthCookies). Clearing only one variant would leave
 * a stale cookie that re-bootstraps a session after logout.
 *
 * A forced logout (sessionExpired) passes notifyServer: false. cy-sso's
 * api/logout ends the whole session family, even when given an old token, so a
 * tab whose refresh was refused would end the student's live session in every
 * other app and tab. Only a Log out button tells cy-sso.
 *
 * @param {string[]} [names=["Authorization", "refresh-token"]]
 * @param {{ notifyServer?: boolean }} [options]
 */
export const clearAuthCookies = (
  names = ["Authorization", "refresh-token"],
  { notifyServer = true } = {},
) => {
  if (notifyServer) notifyBackendLogout();
  names.forEach((name) => deleteCookieAllDomains(name));
  // Raise the cross-app logout signal so an already-open tab of the OTHER app
  // (portal ⇄ sandbox) logs out deterministically. Must run while the session
  // is still readable in localStorage (callers clear storage AFTER this).
  setLoggedOutFlag();
};

/**
 * Shared cross-app logout signal.
 *
 * `cy_logged_out` is the deterministic "the session ended" handshake between the
 * two standalone tabs. Inferring a cross-app logout from API 401 patterns is
 * unreliable (the refresh call can still 200, and unrelated 2xx responses reset
 * any failure counter), so logout/expiry raises this flag on the shared parent
 * domain instead and every other entry point trusts it.
 *
 * Student-only guard: only a STUDENT session participates in cross-app
 * auto-login / auto-logout. Admins and mentors stay app-local, so we never raise
 * the signal for them (a role we can't read defaults to raising it, so a genuine
 * student logout is never silently dropped).
 */
export const LOGGED_OUT_FLAG = "cy_logged_out";

export const setLoggedOutFlag = () => {
  let role;
  try {
    role = decrypt(localStorage.getItem("sandbox_user"))?.role;
  } catch (_) {
    role = undefined;
  }
  if (role && role !== "student") return; // admins / mentors: app-local only

  // Set on the SINGLE canonical shared domain (env-scoped: `.dev.codeyoung.com`
  // on dev, `.codeyoung.com` on prod, host-only on localhost). NOT on broader
  // parents — writing `.codeyoung.com` from a dev host would leak the logout to
  // prod tabs (and vice versa). The sibling app in the same env still sees it.
  setCookie(LOGGED_OUT_FLAG, String(Date.now()), {
    domain: getSharedCookieDomain(),
    maxAge: ONE_YEAR_SECONDS,
    sameSite: "Lax",
  });
};

/** True when a logout signal raised by either app is present. */
export const isLoggedOut = () => !!getCookie(LOGGED_OUT_FLAG);

/** Clears the logout signal — called on a fresh login so the session revives. */
export const clearLoggedOutFlag = () => deleteCookieAllDomains(LOGGED_OUT_FLAG);

/**
 * Returns the most-specific parent domain shared by two hostnames, prefixed
 * with a dot so it can be used as a cookie Domain attribute.
 *
 *   computeSharedCookieDomain("sandbox.codeyoung.com", "studentportal.codeyoung.com")
 *     → ".codeyoung.com"
 *   computeSharedCookieDomain("sandbox.dev.codeyoung.com", "studentportal.dev.codeyoung.com")
 *     → ".dev.codeyoung.com"
 *   computeSharedCookieDomain("localhost", "localhost")
 *     → "localhost"   (no leading dot — cookies on bare localhost are port-agnostic)
 *   computeSharedCookieDomain("a.example.com", "b.other.com")
 *     → null
 *
 * Refuses to return public suffixes ("com", "co.uk") to avoid setting a cookie
 * on a domain we don't control.
 */
export const computeSharedCookieDomain = (hostA, hostB) => {
  if (!hostA || !hostB) return null;
  if (hostA === "localhost" && hostB === "localhost") return "localhost";

  const partsA = hostA.split(".");
  const partsB = hostB.split(".");

  const shared = [];
  let i = partsA.length - 1;
  let j = partsB.length - 1;
  while (i >= 0 && j >= 0 && partsA[i] === partsB[j]) {
    shared.unshift(partsA[i]);
    i--;
    j--;
  }

  // Need at least 2 labels (e.g. "codeyoung.com") to be safe
  if (shared.length < 2) return null;

  return `.${shared.join(".")}`;
};
